import React from "react";
import { Skeleton, Statistic, Progress, Tag } from "antd";
import { formatCurrencyNoTooltip, formatWithCommas } from "../../../assets/helpers";
import { UserSwitchOutlined, FieldTimeOutlined, TeamOutlined, DesktopOutlined, CarOutlined } from "@ant-design/icons";
import "./AllocationBreakdownOverview.scss";

const AllocationBreakdownOverview = ({
  data,
  loading = false,
  format = "absolute",
}) => {
  if (loading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (!data) return null;

  const { overview, allocation_breakdown } = data;
  const money = (v) => formatCurrencyNoTooltip(v, format);

  const getAgentIcon = (type) => {
    switch (type) {
      case "telecaller": return <DesktopOutlined />;
      case "field_agent": return <CarOutlined />;
      default: return <UserSwitchOutlined />;
    }
  };

  const getAgentColor = (type) => {
    switch (type) {
      case "telecaller": return "blue";
      case "field_agent": return "green";
      default: return "default";
    }
  };

  const getAgentStrokeColor = (type) => {
    switch (type) {
      case "telecaller": return "#1890ff";
      case "field_agent": return "#52c41a";
      default: return "#d9d9d9";
    }
  };

  return (
    <div className="allocation-breakdown">
      {/* Top Overview Section */}
      <div className="allocation-breakdown__overview-grid">
        <div className="allocation-breakdown__overview-card">
          <Statistic
            title="Total Cases Allocated"
            value={overview?.total_cases || 0}
            formatter={(val) => formatWithCommas(val)}
          />
        </div>
        <div className="allocation-breakdown__overview-card highlight">
          <Statistic
            title="Total ODPOS Allocated"
            value={overview?.total_odpos || 0}
            formatter={(val) => money(val)}
          />
        </div>
        <div className="allocation-breakdown__overview-card">
          <Statistic
            title="EMI Overdue"
            value={overview?.total_emi_overdue || 0}
            formatter={(val) => money(val)}
            valueStyle={{ color: '#faad14' }}
          />
        </div>
        <div className="allocation-breakdown__overview-card">
          <Statistic
            title="EMI Collected"
            value={overview?.total_emi_collected || 0}
            formatter={(val) => money(val)}
            valueStyle={{ color: '#52c41a' }}
          />
        </div>
        <div className="allocation-breakdown__overview-card">
          <Statistic
            title="Overall Resolution"
            value={overview?.overall_resolved_percent || 0}
            precision={1}
            suffix="%"
            valueStyle={{ color: '#1890ff' }}
          />
        </div>
      </div>

      {/* Agents Breakdown Grid */}
      <div className="allocation-breakdown__agents-grid">
        {allocation_breakdown?.map((agent, index) => (
          <div key={index} className="allocation-breakdown__agent-card">
            <div className="allocation-breakdown__agent-header">
              <h3>
                {getAgentIcon(agent.agent_type)}
                {agent.label}
              </h3>
              <Tag color={getAgentColor(agent.agent_type)} className="agent-tag" icon={<TeamOutlined />}>
                {agent.agents_involved} Agents Active
              </Tag>
            </div>

            <div className="allocation-breakdown__metrics-row">
              <div className="allocation-breakdown__metric">
                <span className="label">Cases</span>
                <span className="value">{formatWithCommas(agent.cases?.count || 0)}</span>
              </div>
              <div className="allocation-breakdown__metric">
                <span className="label">Total ODPOS</span>
                <span className="value">{money(agent.amount?.total_odpos || 0)}</span>
              </div>
              <div className="allocation-breakdown__metric">
                <span className="label">EMI Collected</span>
                <span className="value" style={{ color: '#52c41a' }}>{money(agent.amount?.emi_collected || 0)}</span>
              </div>
            </div>

            <div className="allocation-breakdown__progress-section">
              <div className="progress-label">
                <span>Share of Total Cases</span>
                <span>{agent.cases?.percent_of_total_cases || 0}%</span>
              </div>
              <Progress 
                percent={agent.cases?.percent_of_total_cases || 0} 
                showInfo={false} 
                strokeColor={getAgentStrokeColor(agent.agent_type)}
                size="small"
              />
            </div>

            <div className="allocation-breakdown__progress-section">
              <div className="progress-label">
                <span>Share of Total ODPOS</span>
                <span>{agent.amount?.percent_of_total_odpos || 0}%</span>
              </div>
              <Progress 
                percent={agent.amount?.percent_of_total_odpos || 0} 
                showInfo={false} 
                strokeColor={getAgentStrokeColor(agent.agent_type)}
                size="small"
              />
            </div>

            <div className="allocation-breakdown__resolution">
              <div className="res-stat resolved">
                <span>Resolved</span>
                <span>{formatWithCommas(agent.resolution?.resolved_count || 0)}</span>
              </div>
              <div className="divider" />
              <div className="res-stat unresolved">
                <span>Unresolved</span>
                <span>{formatWithCommas(agent.resolution?.unresolved_count || 0)}</span>
              </div>
              <div className="divider" />
              <div className="res-stat percent">
                <span>Success Rate</span>
                <span>{agent.resolution?.resolved_percent || 0}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllocationBreakdownOverview;
